import { Injectable } from "@angular/core";
import { Apollo } from "apollo-angular";
import { Observable } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { QueryOptions, MutationOptions } from "@apollo/client";
import { ApolloGraphQlService } from "../apollo/apollo-graphql.service";
import gql from "graphql-tag";

const GET_GUNNING_MACHINES = gql`
  {
    findUsuarios {
      uuid
      dispositivos {
        uuid
        nombre
        estructura {
          nombre
          uuid
        }
      }
    }
  }
`;

const GET_CAMPAIGN_GUNNING_MACHINE = gql`
  query findCampaignsByGunningMachine($td: String, $disp: String) {
    findCampaignsByDisp(td: $td, disp: $disp) {
      inicio
      fin
      disp
      duracion
      estado
      nombre
      _id
    }
  }
`;

const GET_CAMPAIGN_BY_ID = gql`
  query findCampaignById($td: String, $id: String) {
    findCampaignById(td: $td, id: $id) {
      inicio
      fin
      disp
      duracion
      estado
      nombre
      _id
    }
  }
`;

const GET_CAMPAIGN_BY_DISP_AND_DATE = gql`
  query findCampaignByDateAndDisp($td: String, $fecha: Float, $disp: String) {
    findCampaignByDateAndDisp(td: $td, fecha: $fecha, disp: $disp) {
      inicio
      fin
      disp
      duracion
      estado
      nombre
      _id
    }
  }
`;

const GET_STEEL_PRODUCTIONS_GUNNING_MACHINE = gql`
  query findSteelProductionsByGunningMachine($td: String, $disp: String) {
    findConsumptionsByDisp(td: $td, disp: $disp) {
      fecha
      disp
      consumo
      coladas
      _id
    }
  }
`;

const GET_STEEL_PRODUCTION_BY_ID = gql`
  query findSteelProductionById($td: String, $id: String) {
    findConsumptionById(td: $td, id: $id) {
      fecha
      disp
      consumo
      coladas
      _id
    }
  }
`;

const GET_OPERATIONS_GUNNING_MACHINE = gql`
  query findOperationsByGunningMachine($td: String, $disp: String) {
    findIntervals(td: $td, disp: $disp) {
      disp
      td
      inicio
      fin
      campos {
        productoGunitadoTotal
        peticionAgua {
          min
          media
          max
          total
        }
      }
      _id
    }
  }
`;

const GET_OPERATIONS_GUNNING_MACHINE_TIME = gql`
  query findOperationsBetweenDates(
    $td: String
    $disp: String
    $inicio: Float
    $fin: Float
  ) {
    findIntervalsBetweenDates(
      td: $td
      disp: $disp
      inicio: $inicio
      fin: $fin
    ) {
      disp
      td
      inicio
      fin
      campos {
        productoGunitadoTotal
        peticionAgua {
          min
          media
          max
          total
        }
      }
      _id
    }
  }
`;

const GET_STEEL_PRODUCTIONS_GUNNING_MACHINE_TIME = gql`
  query findSteelProducionsBetweenDates(
    $td: String
    $disp: String
    $inicio: Float
    $fin: Float
  ) {
    findConsumptionsByDispBetweenDates(
      td: $td
      disp: $disp
      inicio: $inicio
      fin: $fin
    ) {
      fecha
      disp
      consumo
      coladas
      _id
    }
  }
`;

const GET_OPERATION_BY_ID = gql`
  query findOperationById($td: String, $id: String) {
    findIntervalById(td: $td, id: $id) {
      disp
      td
      inicio
      fin
      campos {
        productoGunitadoTotal
        peticionAgua {
          min
          media
          max
          total
        }
      }
      _id
    }
  }
`;

const GET_DATA_TYPES = gql`
  query findDataTypes {
    findTipoDatos {
      uuid
      nombre
    }
  }
`;

const GET_TIME_SERIE = gql`
  query findTimeSerie3(
    $dispositivo: String
    $from: Float
    $to: Float
    $tipoDato: String
  ) {
    findTimeSerie3(
      dispositivo: $dispositivo
      from: $from
      to: $to
      tipoDato: $tipoDato
    ) {
      fecha
      productoGutinado
      peticionAgua
    }
  }
`;

const GET_TIME_SERIE_HEATMAP = gql`
  query findTimeSerie2(
    $dispositivo: String
    $from: Float
    $to: Float
    $tipoDato: String
  ) {
    findTimeSerie2(
      dispositivo: $dispositivo
      from: $from
      to: $to
      tipoDato: $tipoDato
    ) {
      ySector
      acumuladosArray {
        xSector
        data {
          productoGutinado
          peticionAgua
        }
      }
    }
  }
`;

const INSERT_CAMPAIGN = gql`
  mutation insertCampaign(
    $td: String
    $disp: String
    $nombre: String
    $inicio: Float
    $fin: Float
  ) {
    insertCampaign(
      td: $td
      disp: $disp
      nombre: $nombre
      inicio: $inicio
      fin: $fin
    ) {
      insertedId
    }
  }
`;

const DELETE_CAMPAIGN = gql`
  mutation deleteCampaign($td: String, $disp: String, $id: String) {
    deleteCampaign(td: $td, disp: $disp, id: $id) {
      deletedCount
    }
  }
`;

const UPDATE_CAMPAIGN = gql`
  mutation updateCampaign(
    $td: String
    $disp: String
    $id: String
    $nombre: String
    $inicio: Float
    $fin: Float
  ) {
    updateCampaign(
      td: $td
      disp: $disp
      id: $id
      nombre: $nombre
      inicio: $inicio
      fin: $fin
    ) {
      ok
    }
  }
`;

const INSERT_STEEL_PRODUCTION = gql`
  mutation insertSteelProduction(
    $td: String
    $disp: String
    $consumo: Int
    $fecha: Float
    $coladas: Int
  ) {
    insertConsumption(
      td: $td
      disp: $disp
      consumo: $consumo
      fecha: $fecha
      coladas: $coladas
    ) {
      insertedId
    }
  }
`;

const DELETE_STEEL_PRODUCTION = gql`
  mutation deleteSteelProduction($td: String, $disp: String, $id: String) {
    deleteConsumption(td: $td, disp: $disp, id: $id) {
      deletedCount
    }
  }
`;

const UPDATE_STEEL_PRODUCTION = gql`
  mutation updateSteelProduction(
    $td: String
    $disp: String
    $id: String
    $consumo: Int
    $coladas: Int
    $fecha: Float
  ) {
    updateConsumption(
      td: $td
      disp: $disp
      id: $id
      consumo: $consumo
      coladas: $coladas
      fecha: $fecha
    ) {
      ok
    }
  }
`;

const GET_FRIMAG_CONSUMPTIONS_GUNNING_MACHINE = gql`
  query findFrimagConusmptionsByGunningMachine($td: String, $disp: String) {
    findConsumptionsByDisp(td: $td, disp: $disp) {
      fecha
      disp
      consumo
      _id
    }
  }
`;

const GET_FRIMAG_CONSUMPTION_BY_ID = gql`
  query findFrimagConsumptionById($td: String, $id: String) {
    findConsumptionById(td: $td, id: $id) {
      fecha
      disp
      consumo
      _id
    }
  }
`;

const INSERT_FRIMAG_CONSUMPTION = gql`
  mutation insertFrimagConsumption(
    $td: String
    $disp: String
    $consumo: Int
    $fecha: Float
    $coladas: Int
  ) {
    insertConsumption(
      td: $td
      disp: $disp
      consumo: $consumo
      fecha: $fecha
      coladas: $coladas
    ) {
      insertedId
    }
  }
`;

const DELETE_FRIMAG_CONSUMPTION = gql`
  mutation deleteFrimagConsumption($td: String, $disp: String, $id: String) {
    deleteConsumption(td: $td, disp: $disp, id: $id) {
      deletedCount
    }
  }
`;

const UPDATE_FRIMAG_CONSUMPTION = gql`
  mutation updateSteelProduction(
    $td: String
    $disp: String
    $id: String
    $consumo: Int
    $coladas: Int
    $fecha: Float
  ) {
    updateConsumption(
      td: $td
      disp: $disp
      id: $id
      consumo: $consumo
      coladas: $coladas
      fecha: $fecha
    ) {
      ok
    }
  }
`;

const GET_MAGSOL_CONSUMPTIONS_GUNNING_MACHINE = gql`
  query findMagsolConusmptionsByGunningMachine($td: String, $disp: String) {
    findConsumptionsByDisp(td: $td, disp: $disp) {
      fecha
      disp
      consumo
      _id
    }
  }
`;

const GET_MAGSOL_CONSUMPTION_BY_ID = gql`
  query findMagsolConsumptionById($td: String, $id: String) {
    findConsumptionById(td: $td, id: $id) {
      fecha
      disp
      consumo
      _id
    }
  }
`;

const INSERT_MAGSOL_CONSUMPTION = gql`
  mutation insertMagsolConsumption(
    $td: String
    $disp: String
    $consumo: Int
    $fecha: Float
    $coladas: Int
  ) {
    insertConsumption(
      td: $td
      disp: $disp
      consumo: $consumo
      fecha: $fecha
      coladas: $coladas
    ) {
      insertedId
    }
  }
`;

const DELETE_MAGSOL_CONSUMPTION = gql`
  mutation deleteMagsolConsumption($td: String, $disp: String, $id: String) {
    deleteConsumption(td: $td, disp: $disp, id: $id) {
      deletedCount
    }
  }
`;

const UPDATE_MAGSOL_CONSUMPTION = gql`
  mutation updateMagsolConsumption(
    $td: String
    $disp: String
    $id: String
    $consumo: Int
    $coladas: Int
    $fecha: Float
  ) {
    updateConsumption(
      td: $td
      disp: $disp
      id: $id
      consumo: $consumo
      coladas: $coladas
      fecha: $fecha
    ) {
      ok
    }
  }
`;

@Injectable({
  providedIn: "root",
})
export class GunningsService {
  constructor(
    private apollo: Apollo,
    private http: HttpClient,
    private apolloService: ApolloGraphQlService
  ) {}

  getGunningsFromJSONFile(): Observable<any> {
    return this.http.get("../assets/data/gunningsMockup.json");
  }

  getGunningMachineTypeDevices(): Observable<any> {
    return this.apollo.query<any>({
      query: GET_GUNNING_MACHINES,
      fetchPolicy: "network-only",
    });
  }

  getCampaignsByGunningMachine(td: String, disp: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CAMPAIGN_GUNNING_MACHINE,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getCampaignById(td: String, id: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CAMPAIGN_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  getCampaignByDispAndDate(
    td: String,
    fecha: number,
    disp: String
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_CAMPAIGN_BY_DISP_AND_DATE,
      variables: {
        td: td,
        fecha: fecha,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getSteelProductionsByGunningMachine(
    td: String,
    disp: String
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_STEEL_PRODUCTIONS_GUNNING_MACHINE,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getSteelProductionById(td: String, id: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_STEEL_PRODUCTION_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  getOperationsByGunningMachine(td: String, disp: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_OPERATIONS_GUNNING_MACHINE,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getOperationsByGunningMachineBetweenDates(
    td: string,
    disp: string,
    inicio: number,
    fin: number
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_OPERATIONS_GUNNING_MACHINE_TIME,
      variables: {
        td: td,
        disp: disp,
        inicio: inicio,
        fin: fin,
      },
      fetchPolicy: "network-only",
    });
  }

  getOperationById(td: string, id: string) {
    return this.apollo.query<any>({
      query: GET_OPERATION_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  getDataTypes(): Observable<any> {
    return this.apollo.query<any>({
      query: GET_DATA_TYPES,
      fetchPolicy: "network-only",
    });
  }

  getTimeseriesData(disp, td, from, to): Observable<any> {
    return this.apollo.query<any>({
      query: GET_TIME_SERIE,
      variables: {
        tipoDato: td,
        dispositivo: disp,
        from: from,
        to: to,
      },
      fetchPolicy: "network-only",
    });
  }

  getTimeseriesHeatMapData(disp, td, from, to): Observable<any> {
    return this.apollo.query<any>({
      query: GET_TIME_SERIE_HEATMAP,
      variables: {
        tipoDato: td,
        dispositivo: disp,
        from: from,
        to: to,
      },
      fetchPolicy: "network-only",
    });
  }

  insertCampaignByGunningMachine(
    collection,
    disp,
    inicio,
    fin,
    nombre
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: INSERT_CAMPAIGN,
      variables: {
        td: collection,
        disp: disp,
        inicio: new Date(inicio).getTime() / 1000,
        fin: new Date(fin).getTime() / 1000,
        nombre: nombre,
      },
    });
  }

  deleteCampaignById(collection, disp, id): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: DELETE_CAMPAIGN,
      variables: {
        td: collection,
        disp: disp,
        id: id,
      },
    });
  }

  updateCampaignById(
    collection,
    disp,
    id,
    inicio,
    fin,
    nombre
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_CAMPAIGN,
      variables: {
        td: collection,
        disp: disp,
        id: id,
        inicio: new Date(inicio).getTime() / 1000,
        fin: new Date(fin).getTime() / 1000,
        nombre: nombre,
      },
    });
  }

  getSteelProductionsByGunningMachineBetweenDates(
    td: string,
    disp: string,
    inicio: number,
    fin: number
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_STEEL_PRODUCTIONS_GUNNING_MACHINE_TIME,
      variables: {
        td: td,
        disp: disp,
        inicio: inicio,
        fin: fin,
      },
      fetchPolicy: "network-only",
    });
  }

  insertSteelProductionByGunningMachine(
    collection,
    disp,
    consumo,
    fecha,
    coladas
  ): Observable<any> {
    console.log(disp);
    return this.apollo.mutate<any>({
      mutation: INSERT_STEEL_PRODUCTION,
      variables: {
        td: collection,
        disp: disp,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
        coladas: coladas,
      },
    });
  }

  deleteSteelProductionById(collection, disp, id): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: DELETE_STEEL_PRODUCTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
      },
    });
  }

  updateSteelProductionById(
    collection,
    disp,
    id,
    fecha,
    consumo,
    coladas
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_STEEL_PRODUCTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
        coladas: coladas,
      },
    });
  }

  getFrimagConsumptionsByGunningMachine(
    td: String,
    disp: String
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_FRIMAG_CONSUMPTIONS_GUNNING_MACHINE,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getFrimagConsumptionById(td: String, id: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_FRIMAG_CONSUMPTION_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  insertFrimagConsumptionByGunningMachine(
    collection,
    disp,
    consumo,
    fecha
  ): Observable<any> {
    console.log(disp);
    return this.apollo.mutate<any>({
      mutation: INSERT_FRIMAG_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
      },
    });
  }

  deleteFrimagConsumptionById(collection, disp, id): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: DELETE_FRIMAG_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
      },
    });
  }

  updateFrimagConsumptionById(
    collection,
    disp,
    id,
    fecha,
    consumo
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_FRIMAG_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
      },
    });
  }

  getMagsolConsumptionsByGunningMachine(
    td: String,
    disp: String
  ): Observable<any> {
    return this.apollo.query<any>({
      query: GET_MAGSOL_CONSUMPTIONS_GUNNING_MACHINE,
      variables: {
        td: td,
        disp: disp,
      },
      fetchPolicy: "network-only",
    });
  }

  getMagsolConsumptionById(td: String, id: String): Observable<any> {
    return this.apollo.query<any>({
      query: GET_MAGSOL_CONSUMPTION_BY_ID,
      variables: {
        td: td,
        id: id,
      },
      fetchPolicy: "network-only",
    });
  }

  insertMagsolConsumptionByGunningMachine(
    collection,
    disp,
    consumo,
    fecha
  ): Observable<any> {
    console.log(disp);
    return this.apollo.mutate<any>({
      mutation: INSERT_MAGSOL_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
      },
    });
  }

  deleteMagsolConsumptionById(collection, disp, id): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: DELETE_MAGSOL_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
      },
    });
  }

  updateMagsolConsumptionById(
    collection,
    disp,
    id,
    fecha,
    consumo
  ): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: UPDATE_MAGSOL_CONSUMPTION,
      variables: {
        td: collection,
        disp: disp,
        id: id,
        fecha: new Date(fecha).getTime() / 1000,
        consumo: consumo,
      },
    });
  }
}
